/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Link } from 'react-router';

import Drawer from 'common/components-v2/modal/Drawer';
import { useDocumentTypesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import { SimpleDataGrid } from 'common/components/data-grid/SimpleDataGrid';
import { parseFullAddressStringFromAddressObject } from '../../services/AddressService';

import styles from './DocumentsPhotoModal.module.scss';

const DocumentsPhotoModal = props => {
    const { data: documentTypes } = useDocumentTypesQuery();

    const genUrl = row => {
        const raw = row?._raw || row;
        switch (raw?.documentTypeId) {
            case 'WORK_ORDER':
                return `/ops/work-orders/${raw.id}/details`;
            case 'QUOTE':
                return `/ops/quotes/${raw.id}/details`;
            default:
                return '';
        }
    };

    const withPipe = label => ({
        renderHeader: () => (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: '100%',
                    padding: '0 6px',
                    gap: 10,
                    boxSizing: 'border-box',
                    fontWeight: 700,
                    fontSize: 14,
                }}
            >
                <span>{label}</span>
                <span
                    style={{
                        color: '#bdbdbd',
                        fontWeight: 400,
                        fontSize: 18,
                        lineHeight: 1,
                    }}
                >
                    |
                </span>
            </div>
        ),
    });

    const columns = useMemo(
        () => [
            {
                field: 'jobNumber',
                headerName: 'Job Number',
                ...withPipe('Job Number'),
                flex: 1,
                minWidth: 140,
                sortable: false,
            },
            {
                field: 'number',
                headerName: 'Doc Number',
                ...withPipe('Doc Number'),
                flex: 1,
                minWidth: 170,
                sortable: false,
                renderCell: params => {
                    const row = params?.row || {};
                    const raw = row?._raw;
                    const url = raw ? genUrl(raw) : '';
                    const number = params.value || '';

                    return url ? (
                        <Link to={url} target='_blank'>
                            {number}
                        </Link>
                    ) : (
                        number
                    );
                },
            },
            {
                field: 'documentTypeId',
                headerName: 'Doc Type',
                ...withPipe('Doc Type'),
                flex: 1,
                minWidth: 170,
                sortable: false,
                renderCell: params => {
                    const typeId = params?.row?.documentTypeId || '';
                    const found = documentTypes?.find(t => t.id === typeId);
                    return found?.display || typeId;
                },
            },
            {
                field: 'clientName',
                headerName: 'Client Name',
                ...withPipe('Client Name'),
                flex: 1,
                minWidth: 160,
                sortable: false,
            },
            {
                field: 'address',
                headerName: 'Address',
                flex: 2,
                minWidth: 280,
                sortable: false,
            },
        ],
        [documentTypes]
    );

    const rows = useMemo(() => {
        const docs = Array.isArray(props.documents) ? props.documents : [];
        return docs.map((d, idx) => ({
            id: d?.id ?? `${d?.documentTypeId || 'DOC'}-${idx}`,
            jobNumber: d?.job?.number || '',
            number: d?.number || '',
            documentTypeId: d?.documentTypeId || '',
            clientName:
                typeof d?.client === 'string'
                    ? d.client
                    : d?.client?.name || '',
            address: d?.property
                ? parseFullAddressStringFromAddressObject(d.property, true)
                : '',
            _raw: d,
        }));
    }, [props.documents]);

    return (
        <Drawer
            open
            onClose={props.handleCloseOnClick}
            closeByDocument
            showClose
            drawerStyle={{ width: '45%' }}
            title={
                <span style={{ fontSize: 24, fontWeight: 600 }}>
                    {`Photo ${props.metadata?.fileName || props.metadata?.hashId || ''} Reused Instances`}
                </span>
            }
        >
            <div className={styles.body}>
                <SimpleDataGrid
                    rows={rows}
                    columns={columns}
                    disableColumnResize
                    autoHeight
                    customSx={{
                        '& .MuiDataGrid-cell': {
                            fontSize: 14,
                            borderRight: 'none',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            padding: '0 !important',
                            height: '40px !important',
                        },

                        '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' },

                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                            padding: '0 !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                            width: '100%',
                            height: '100%',
                        },
                        '& .MuiDataGrid-iconSeparator': { display: 'none' },
                    }}
                />
            </div>
        </Drawer>
    );
};

DocumentsPhotoModal.propTypes = {
    handleCloseOnClick: PropTypes.func.isRequired,
    documents: PropTypes.array.isRequired,
    metadata: PropTypes.object.isRequired,
};

export default DocumentsPhotoModal;
